var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { validationFailure, validationOk, } from "@hyper-hyper-space/hhs3_mvt";
import { verifyPayloadSignature, deserializePublicKeyFromBase64, computeKeyId } from "../../authorship.js";
import { createRCapFormat, addIdentityFormat, createCapabilityFormat, deleteCapabilityFormat, grantFormat, MAX_CAP_ORIGINS, revokeFormat, } from "./payload.js";
export function validateRCapPayload(payload, context) {
    return __awaiter(this, void 0, void 0, function* () {
        if (typeof payload !== "object" || payload === null || Array.isArray(payload))
            return validationFailure("RCap payload must be an object");
        if (typeof payload["action"] !== "string")
            return validationFailure("RCap payload action must be a string");
        const action = payload["action"];
        if (context.mode === "create") {
            if (action !== "create")
                return validationFailure("RCap creation action must be 'create'");
            return validateCreate(payload, context.ctx);
        }
        if (action === "create")
            return validationFailure("create payload cannot be applied to an existing RCap");
        switch (action) {
            case "add-identity": return validateAddIdentity(payload, context.cap, context.at);
            case "create-cap": return validateCreateCap(payload, context.cap, context.at);
            case "delete-cap": return validateDeleteCap(payload, context.cap, context.at);
            case "grant": return validateGrant(payload, context.cap, context.at);
            case "revoke": return validateRevoke(payload, context.cap, context.at);
            default: return validationFailure(`unknown RCap action '${action}'`);
        }
    });
}
function validateCreate(payload, ctx) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(createRCapFormat, payload))
            return validationFailure("RCap create payload format is invalid");
        const cp = payload;
        if (cp.creators.length === 0)
            return validationFailure("RCap create must have at least one creator");
        if (cp.creators.length !== cp.creatorKeys.length)
            return validationFailure("RCap creators and creatorKeys lengths differ");
        const hashSuite = ctx.getHashSuite();
        for (let i = 0; i < cp.creators.length; i++) {
            try {
                const pk = deserializePublicKeyFromBase64(cp.creatorKeys[i]);
                if (computeKeyId(pk, hashSuite) !== cp.creators[i]) {
                    return validationFailure(`creator keyId '${cp.creators[i]}' does not match creator public key`);
                }
            }
            catch (_a) {
                return validationFailure(`creator key '${cp.creators[i]}' is invalid`);
            }
        }
        const capNames = new Set(Object.keys(cp.initialCaps));
        for (const def of Object.values(cp.initialCaps)) {
            for (const mgr of def.managedBy) {
                if (mgr !== "creator" && !capNames.has(mgr))
                    return validationFailure(`initial capability manager '${mgr}' does not exist`);
            }
        }
        if (cp.enrollCapability !== undefined) {
            if (!capNames.has(cp.enrollCapability))
                return validationFailure(`enroll capability '${cp.enrollCapability}' does not exist`);
        }
        return validationOk();
    });
}
function verifySignedPayload(payload, cap) {
    return __awaiter(this, void 0, void 0, function* () {
        const keyLookup = (keyId) => cap.lookupKey(keyId);
        return verifyPayloadSignature(payload, keyLookup);
    });
}
function validateAddIdentity(payload, cap, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(addIdentityFormat, payload))
            return validationFailure("add-identity payload format is invalid");
        const p = payload;
        try {
            const pk = deserializePublicKeyFromBase64(p.publicKey);
            if (computeKeyId(pk, cap.getHashSuite()) !== p.keyId)
                return validationFailure(`identity keyId '${p.keyId}' does not match public key`);
        }
        catch (_a) {
            return validationFailure(`identity public key for '${p.keyId}' is invalid`);
        }
        if (!(yield verifySignedPayload(payload, cap)))
            return validationFailure("add-identity signature could not be verified");
        const authorId = p.author;
        if (!cap.isCreator(authorId)) {
            const view = yield cap.getView(at, at);
            if (!(yield view.hasCapability(authorId, cap.getEnrollCapabilityName()))) {
                return validationFailure(`author '${authorId}' lacks enroll capability`);
            }
        }
        return validationOk();
    });
}
function validateCreateCap(payload, cap, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(createCapabilityFormat, payload))
            return validationFailure("create-cap payload format is invalid");
        const p = payload;
        if (!(yield verifySignedPayload(payload, cap)))
            return validationFailure("create-cap signature could not be verified");
        if (!cap.isCreator(p.author))
            return validationFailure(`author '${p.author}' is not a creator`);
        const view = yield cap.getView(at, at);
        if (yield view.capabilityExists(p.capName))
            return validationFailure(`capability '${p.capName}' already exists`);
        for (const mgr of p.managedBy) {
            if (mgr !== "creator" && !(yield view.capabilityExists(mgr)))
                return validationFailure(`manager capability '${mgr}' does not exist`);
        }
        return validationOk();
    });
}
function validateDeleteCap(payload, cap, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(deleteCapabilityFormat, payload))
            return validationFailure("delete-cap payload format is invalid");
        const p = payload;
        if (!(yield verifySignedPayload(payload, cap)))
            return validationFailure("delete-cap signature could not be verified");
        if (!cap.isCreator(p.author))
            return validationFailure(`author '${p.author}' is not a creator`);
        const view = yield cap.getView(at, at);
        if (!(yield view.capabilityExists(p.capName)))
            return validationFailure(`capability '${p.capName}' does not exist`);
        return validationOk();
    });
}
function validateGrant(payload, cap, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(grantFormat, payload))
            return validationFailure("grant payload format is invalid");
        const p = payload;
        if (!(yield verifySignedPayload(payload, cap)))
            return validationFailure("grant signature could not be verified");
        const authorId = p.author;
        const view = yield cap.getView(at, at);
        if (!(yield view.capabilityExists(p.capName)))
            return validationFailure(`capability '${p.capName}' does not exist`);
        if (!(yield view.isIdentity(p.grantee)))
            return validationFailure(`grantee '${p.grantee}' is not an identity`);
        if (!cap.isCreator(authorId)) {
            const managedBy = yield view.getManagedBy(p.capName);
            let authorized = false;
            for (const mgr of managedBy) {
                if (mgr === "creator")
                    continue;
                if (yield view.hasCapability(authorId, mgr)) {
                    authorized = true;
                    break;
                }
            }
            if (!authorized)
                return validationFailure(`author '${authorId}' is not authorized to manage '${p.capName}'`);
        }
        const expected = Array.from(yield view.currentCapCreationVersion(p.capName))
            .sort()
            .slice(0, MAX_CAP_ORIGINS);
        if (expected.length !== p.capOrigins.length)
            return validationFailure(`grant origins for '${p.capName}' do not match current capability version`);
        for (let i = 0; i < expected.length; i++) {
            if (expected[i] !== p.capOrigins[i])
                return validationFailure(`grant origins for '${p.capName}' do not match current capability version`);
        }
        return validationOk();
    });
}
function validateRevoke(payload, cap, at) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!json.checkFormat(revokeFormat, payload))
            return validationFailure("revoke payload format is invalid");
        const p = payload;
        if (!(yield verifySignedPayload(payload, cap)))
            return validationFailure("revoke signature could not be verified");
        const authorId = p.author;
        const view = yield cap.getView(at, at);
        if (!(yield view.capabilityExists(p.capName)))
            return validationFailure(`capability '${p.capName}' does not exist`);
        if (!cap.isCreator(authorId)) {
            const managedBy = yield view.getManagedBy(p.capName);
            let authorized = false;
            for (const mgr of managedBy) {
                if (mgr === "creator")
                    continue;
                if (yield view.hasCapability(authorId, mgr)) {
                    authorized = true;
                    break;
                }
            }
            if (!authorized)
                return validationFailure(`author '${authorId}' is not authorized to manage '${p.capName}'`);
        }
        return validationOk();
    });
}
